/**
 * One run, as a horizontal strip — the run detail for the top of the runs pane while the activation
 * failure chat is open, and the horizontal counterpart of `RunOverview`.
 *
 * WHY IT EXISTS. With the chat open, the chat takes the page's right-hand pane and the run detail
 * column (`RunOverview`) is hidden (spec decision 8), so the reader lost the run's funnel, its status
 * and its connection settings exactly while asking why it failed. The strip puts them back above the
 * table, where they cost height instead of width (mockup "A · Funnel strip above the table"). The same
 * strip is the run detail wherever the column does not fit beside the table with the chat closed — a
 * narrow window, a phone (`RunsPanel`'s `runDetailLayout`) — and then it carries `Ask why` too.
 *
 * A VARIATION, NOT A FORK. Everything on it is rendered by the components the detail column uses:
 * `PhaseStage` (horizontal), `PhaseNode`, `PhaseLeg` (the spine's leg treatments, turned
 * across), `DeliveryProgress`, `ConnectionSettingsLine`, `SyncStatusBadge`,
 * `RunShortfall`, `RunErrorMessage`, and `RunLabels` for every word on the run line. The phases come from
 * the same `deriveRunPhases`, so the strip and the column can never disagree about a run. This file
 * composes and derives nothing of its own beyond the failed count on the badge.
 *
 * ONE LAYOUT, A STEPPER, AT EVERY WIDTH THE STRIP GETS — down to a phone with the chat closed (about
 * 21rem at 390px). Row 1 is the three marks as nodes across
 * the strip's whole content width — Extract flush left, Plan centred, Send flush right — with each leg
 * stretched between two nodes in the vertical connector's treatment for that leg's state
 * (`PhaseLeg`, shared with the spine). Row 2 is three equal columns, one text BLOCK per stage that
 * follows its node: Extract's anchored at the left edge, Plan's centred on the middle node, Send's
 * anchored at the right edge. Each block is as wide as its text (capped at its column) and its lines
 * are always LEFT-aligned inside it, so wrapped text keeps one clean edge. Nothing truncates but the
 * two status lines (clamped to two lines, with the full text as a `title`); the `of … · %` part and
 * the breakdown wrap, the breakdown only between its `name value` pairs. The strip (the card) is its own
 * `@container`, and only spacing steps on it: below 36rem the gutters and leg margins tighten, and below
 * 26rem (a phone) they tighten again, the stage numbers drop one type step and the connection band's
 * mapping rows wrap instead of cutting off. Numbers, labels and the `N failed` badge never truncate.
 *
 * ACCESSIBILITY. The run line carries an `<h2>` (beside the toolbar's `Sync runs`), built from `runStartedLabel(run)` with the
 * same `Run from …` name `RunIdentityCard` uses, which ties it to the table row that opened it (the
 * detail column's heading is hidden while the strip shows). The stages are an ordered list, so the
 * reading order is the pipeline's. `Edit` is the band's own button.
 */
import { MessageSquareText } from 'lucide-react';
import { Fragment } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Card } from '@/_shared/components/ui/card';
import { cn } from '@/_shared/lib/utils';

import { deriveRunPhases, failedPhase, PHASE_LABEL, type RunPhase } from '../lib/run-phases';
import { exact, formatDurationSeconds, isRunning, runDurationSeconds, runHasFailures } from '../lib/run-view';
import { useTickingNow } from '../lib/use-ticking-now';
import type { IdentifierMapping, RawSyncRun } from '../types';
import { ConnectionSettingsLine } from './ConnectionSettingsBand';
import { DeliveryProgress } from './DeliveryProgress';
import { PhaseLeg, PhaseNode, PhaseStage } from './PhaseStage';
import { RunErrorMessage } from './RunErrorMessage';
import { runStartedLabel, runTriggerLabel, runTypeLabel } from './RunLabels';
import { RunShortfall } from './RunShortfall';
import { SyncStatusBadge } from './SyncStatusBadge';

interface RunStripProps {
  run: RawSyncRun;
  /** Registry key for the destination's brand mark, e.g. `FB_CUSTOM_AUDIENCE`. Send's stage only. */
  destinationDefinitionName?: string | undefined;
  /** `RawSqlAudience.sourceDefinitionName`, for Extract's mark. See `RunOverview`. */
  warehouseName?: string | undefined;
  /** `sync.config.destination.syncMode` — connection-scoped; see `ConnectionSettingsBand`. */
  syncMode?: string | undefined;
  /** This connection's own retention, already resolved by `retentionDays(sync)`. `null` is OFF. */
  retentionDays: number | null;
  /** `sync.config.destination.identifierMappings` — see `ConnectionSettingsBand` for the three states. */
  identifierMappings?: IdentifierMapping[] | undefined;
  /** `sync.config.destination.fieldMappings` — LinkedIn's or Gainsight PX's second table. */
  fieldMappings?: IdentifierMapping[] | undefined;
  /** The second table's name for this destination. */
  fieldMappingsLabel?: string | undefined;
  /** Opens the sync's edit sheet, from the band's `Edit`. */
  onEditConnection?: (() => void) | undefined;
  /** Why `Edit` is unavailable; see the band's own prop. */
  editConnectionDisabledReason?: string | undefined;
  /**
   * Opens the failure chat on this run. Set by `RunsPanel` only while the chat is CLOSED (the strip then
   * stands in for the run detail column in a narrow pane), and rendered, as on `RunOverview`, only when
   * this run failed records or failed outright.
   */
  onAskWhy?: (() => void) | undefined;
  className?: string;
}

/** The text under each node follows its node: left, centre, right. */
const STAGE_ALIGN = ['start', 'center', 'end'] as const;

/**
 * The records the destination rejected, for the badge beside `accepted`. Settled runs only: while a
 * run is live the count is still moving and the Send stage already says so.
 */
function failedRecords(run: RawSyncRun): number {
  if (isRunning(run)) return 0;
  return run.metrics?.failed?.total ?? 0;
}

export function RunStrip({
  run,
  destinationDefinitionName,
  warehouseName,
  syncMode,
  retentionDays,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel,
  onEditConnection,
  editConnectionDisabledReason,
  onAskWhy,
  className,
}: RunStripProps): React.ReactElement {
  const phases = deriveRunPhases(run);
  const running = isRunning(run);
  const now = useTickingNow(running);
  const duration = formatDurationSeconds(runDurationSeconds(run, now));
  const failed = failedPhase(run);
  const failedCount = failedRecords(run);
  // The mockup's run line: when, then how it was started, what kind of run, and how long it took.
  const meta = `${runTriggerLabel(run)} · ${runTypeLabel(run)} · ${duration}`;
  const failedBadge = (phase: RunPhase): React.ReactNode =>
    phase.phase === 'send' && failedCount > 0 ? (
      <Badge variant="error" bordered data-slot="failed-badge" className="shrink-0">
        {exact(failedCount)} failed
      </Badge>
    ) : null;

  return (
    <Card data-slot="run-strip" className={cn('@container gap-0 p-0', className)}>
      <div className="flex flex-col gap-2.5 px-2.5 py-3 @[26rem]:px-3 @[36rem]:px-3.5">
        {/* The run line. Wraps: on a narrow pane the badge drops under the date rather than squeezing it. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2
            className="text-[0.8125rem] leading-[1.125rem] font-semibold text-foreground"
            aria-label={`Run from ${runStartedLabel(run)}`}
          >
            {runStartedLabel(run)}
          </h2>
          <p className="min-w-0 text-xs text-muted-foreground">{meta}</p>
          <SyncStatusBadge run={run} bordered className="ml-auto max-w-full shrink-0 whitespace-normal" />
        </div>

        {/* Clamped to two lines with Show more, copyable, and never wider than the strip — see
            `RunErrorMessage` for the errors it is built for. Keyed by run, so selecting another run
            starts it clamped again instead of carrying the last run's Show less over. */}
        {run.error ? (
          <RunErrorMessage
            key={run.run_id}
            error={run.error}
            prefix={failed ? `${PHASE_LABEL[failed]} failed: ` : 'Sync error: '}
          />
        ) : null}

        {/* ROW 1, THE TRACK, across the strip's whole content width: Extract's mark flush left, Send's
            flush right, Plan's in the exact centre (the two legs are equal `flex-1` segments between
            three marks of one size), and each leg in the vertical connector's own treatment for that
            leg's state, turned across (`PhaseLeg`). Decorative as a whole: the marks are `aria-hidden`
            already, and the text row below carries every word. */}
        <div aria-hidden data-slot="run-track" className="flex min-w-0 items-center">
          {phases.map((phase, index) => (
            <Fragment key={phase.phase}>
              <PhaseNode
                phase={phase}
                warehouseName={warehouseName}
                destinationDefinitionName={destinationDefinitionName}
              />
              {index < phases.length - 1 && phase.legOut !== null ? (
                <PhaseLeg leg={phase.legOut} className="mx-1 min-w-3 flex-1 @[26rem]:mx-1.5 @[36rem]:mx-2" />
              ) : null}
            </Fragment>
          ))}
        </div>
        {/* ROW 2, THE TEXT: three equal columns locked to the nodes above — Extract's block anchored
            left under the left mark, Plan's centred on the middle one, Send's (with its `N failed`
            badge) anchored right under the right one; left-aligned lines inside each, in pipeline
            order. */}
        <ol
          aria-label="Sync stages"
          className="m-0 -mt-1 grid list-none grid-cols-3 gap-x-1.5 p-0 @[26rem]:gap-x-2 @[36rem]:gap-x-4"
        >
          {phases.map((phase, index) => (
            <li key={phase.phase} className="min-w-0">
              <PhaseStage
                orientation="horizontal"
                align={STAGE_ALIGN[index] ?? 'start'}
                phase={phase}
                destinationDefinitionName={destinationDefinitionName}
                aside={failedBadge(phase)}
              />
            </li>
          ))}
        </ol>

        {/* The phase card's delivery bar, as it is — its gate decides (a live run over one batch, past
            one reporting flush) and it returns null on every other run. Full width here, and no top
            margin of its own: the strip's `gap-2.5` already spaces it from the stages. */}
        <DeliveryProgress run={run} className="mt-0 w-full" />

        {/* `pt-0`: the column's `pt-3` is the gap under the phase card; here the strip's own `gap-2.5`
            already separates it from the stages. */}
        <RunShortfall run={run} className="pt-0" />

        {/* Where `RunOverview` puts it: after the stages and the shortfall that describe what failed. */}
        {onAskWhy && runHasFailures(run) ? (
          <div>
            <Button
              variant="secondary"
              size="md"
              Icon={<MessageSquareText aria-hidden />}
              onClick={onAskWhy}
              data-chat-entry=""
            >
              Ask why
            </Button>
          </div>
        ) : null}
      </div>

      <ConnectionSettingsLine
        syncMode={syncMode}
        retentionDays={retentionDays}
        identifierMappings={identifierMappings}
        fieldMappings={fieldMappings}
        fieldMappingsLabel={fieldMappingsLabel}
        onEdit={onEditConnection}
        editDisabledReason={editConnectionDisabledReason}
      />
    </Card>
  );
}
